// TagInput.tsx
import React, { useId, ReactNode, useMemo, useCallback } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react';
import { clsx } from 'clsx';
import { FormField } from './FormField';
import { BaseFormField } from 'types/form/baseFormField';
import { ChevronDown, X } from 'lucide-react';

export interface TagInputItem<K = number | string> {
  key: K;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface BaseTagInputProps<K = number | string> {
  items: TagInputItem<K>[];
  selectedItems: TagInputItem<K>[];
  placeholder?: string;
  disabled?: boolean;
  onSelect?: (selectedItems: TagInputItem<K>[]) => void;
  variant?: 'default' | 'tiny' | 'medium' | 'no-border';
  className?: string;
  maxTags?: number;
}

interface TagInputProps extends BaseFormField, BaseTagInputProps { }

const TagInput = ({
  label,
  error,
  required,
  name,
  ...props
}: TagInputProps) => (
  <FormField label={label} error={error} required={required} name={name}>
    <TagInputBase {...props} />
  </FormField>
);

const TagInputBase = <K extends string | number>({
  selectedItems,
  items,
  onSelect,
  placeholder,
  disabled,
  variant = 'default',
  className,
  maxTags
}: BaseTagInputProps<K>) => {
  const id = useId();

  const handleToggleItem = useCallback((item: TagInputItem<K>) => {
    if (!onSelect) return;

    const isSelected = selectedItems.some(selected => selected.key === item.key);

    if (isSelected) {
      // Retirer l'item
      onSelect(selectedItems.filter(selected => selected.key !== item.key));
    } else {
      // Ajouter l'item (vérifier maxTags)
      if (maxTags !== undefined && selectedItems.length >= maxTags) return;
      onSelect([...selectedItems, item]);
    }
  }, [selectedItems, onSelect, maxTags]);

  const handleRemoveTag = useCallback((e: React.MouseEvent, itemToRemove: TagInputItem<K>) => {
    e.stopPropagation();
    if (!onSelect || disabled) return;
    onSelect(selectedItems.filter(item => item.key !== itemToRemove.key));
  }, [selectedItems, onSelect, disabled]);

  const getButtonClasses = useCallback((open: boolean) => clsx(
    "w-full text-base sm:text-sm/6 flex flex-wrap gap-2 items-center border border-gray-300 px-4 py-2 min-h-12 rounded-md bg-white cursor-pointer transition-colors duration-200",
    disabled && "bg-gray-100 cursor-not-allowed",
    open && "rounded-b-none",
    {
      'border-none': variant === 'no-border',
      'shadow-none border-gray-300': variant === 'tiny' || variant === 'medium',
    },
    className
  ), [disabled, variant, className]);

  const menuClasses = useMemo(() => clsx(
    "absolute max-h-[200px] w-full bg-white z-50 top-full overflow-y-auto border border-gray-300 rounded-b-md"
  ), []);

  const getItemClasses = useCallback((focus: boolean, selected: boolean, itemDisabled?: boolean) => clsx(
    "px-4 py-4 flex justify-between items-center whitespace-nowrap cursor-pointer font-bold font-lato overflow-hidden text-ellipsis m-0 text-primary",
    (focus || selected) && !itemDisabled && "bg-primary text-white",
    itemDisabled && "opacity-50 cursor-not-allowed",
    "border-b border-gray-100 last:border-b-0"
  ), []);

  const containerClasses = useMemo(() => clsx(
    "w-full relative",
    disabled && "opacity-50"
  ), [disabled]);

  const chevronClasses = useCallback((open: boolean) => clsx(
    "w-6 h-6 text-primary transition-transform duration-200 ml-auto",
    open && "rotate-180"
  ), []);

  const tagClasses = useMemo(() => clsx(
    "inline-flex items-center gap-1 px-3 py-1 bg-primary text-white rounded-full text-sm font-medium",
    !disabled && "hover:bg-primary/90"
  ), [disabled]);

  const isItemSelected = useCallback((item: TagInputItem<K>) => {
    return selectedItems.some(selected => selected.key === item.key);
  }, [selectedItems]);

  const isMaxReached = maxTags !== undefined && selectedItems.length >= maxTags;

  return (
    <Listbox
      as="div"
      value={selectedItems}
      onChange={() => { }}
      className={containerClasses}
      multiple
    >
      {({ open }) => (
        <>
          <ListboxButton
            id={id}
            className={getButtonClasses(open)}
            disabled={disabled}
          >
            <div className="flex flex-wrap gap-2 flex-1 items-center">
              {selectedItems.length === 0 ? (
                <span className="text-gray-400">{placeholder || 'Sélectionner...'}</span>
              ) : (
                selectedItems.map(item => (
                  <span key={item.key} className={tagClasses}>
                    {item.icon && (
                      <span className="w-4 h-4">
                        {React.isValidElement(item.icon) &&
                          React.cloneElement(item.icon)}
                      </span>
                    )}
                    <span>{item.label}</span>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => handleRemoveTag(e, item)}
                        className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        aria-label={`Retirer ${item.label}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
            <ChevronDown className={chevronClasses(open)} />
          </ListboxButton>

          <ListboxOptions className={menuClasses}>
            {items.map(item => {
              const selected = isItemSelected(item);
              const itemDisabled = item.disabled || (isMaxReached && !selected);

              return (
                <ListboxOption
                  key={item.key}
                  value={item}
                  disabled={itemDisabled}
                  onClick={() => !itemDisabled && handleToggleItem(item)}
                  className={({ focus }) =>
                    getItemClasses(focus, selected, itemDisabled)
                  }
                >
                  <div className="flex items-center gap-2">
                    {item.icon && (
                      <span className="w-4 h-4">
                        {React.isValidElement(item.icon) &&
                          React.cloneElement(item.icon)}
                      </span>
                    )}
                    <span>{item.label}</span>
                  </div>
                  {selected && (
                    <span className="ml-2">✓</span>
                  )}
                </ListboxOption>
              );
            })}
          </ListboxOptions>
        </>
      )}
    </Listbox>
  );
};

export { TagInput, TagInputBase };
